import { el } from './ui'
import { reducedMotion } from './motion'
import { lineDrawing } from './picture-tiles'

// The approved production mix is fixed. The prototype's switches and saved
// experiment preferences must not change what customers see.
export const messageView = { focus: false, dates: true, context: true, feedback: true, extra: true, comfort: false }

export function messageSwitch(label: string, checked: boolean, change: () => void): HTMLButtonElement {
  const button = el('button', { type: 'button', class: 'messages-switch', role: 'switch', 'aria-label': label, 'aria-checked': String(checked) },
    el('span', { class: 'messages-switch-knob', 'aria-hidden': 'true' }, '✓'))
  button.addEventListener('click', change)
  return button
}

const disclosures = new Map<HTMLDetailsElement, { animation: Animation; settle: (open: boolean) => void }>()
export function finishDisclosures(): void {
  for (const [node, motion] of disclosures) motion.settle(node.dataset.closing !== 'true')
}

export function setDisclosure(node: HTMLDetailsElement, open: boolean): void {
  const summary = node.querySelector('summary')
  if (!summary) return
  const start = node.getBoundingClientRect().height
  const previous = disclosures.get(node)
  if (previous) { previous.animation.cancel(); disclosures.delete(node) }
  const reset = (): void => {
    node.style.height = ''; node.style.overflow = ''; node.style.boxSizing = ''
    delete node.dataset.closing
    if (!disclosures.size) document.documentElement.classList.remove('messages-disclosing')
  }
  if (!messageView.extra || reducedMotion() || typeof node.animate !== 'function') {
    node.open = open; reset(); return
  }
  // Pin before opening: measuring must never briefly expand the whole card.
  node.style.boxSizing = 'border-box'
  node.style.height = `${start}px`
  node.style.overflow = 'hidden'
  node.open = true
  node.dataset.closing = String(!open)
  document.documentElement.classList.add('messages-disclosing')
  const style = getComputedStyle(node)
  const border = (parseFloat(style.borderTopWidth) || 0) + (parseFloat(style.borderBottomWidth) || 0)
  const end = open ? node.scrollHeight + border : summary.getBoundingClientRect().height + border + (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0)
  const animation = node.animate([{ height: `${start}px` }, { height: `${end}px` }], { duration: 200, easing: 'cubic-bezier(.2,.8,.2,1)', fill: 'forwards' })
  const settle = (value: boolean): void => {
    if (disclosures.get(node)?.animation !== animation) return
    disclosures.delete(node); node.open = value; animation.cancel(); reset()
  }
  disclosures.set(node, { animation, settle })
  void animation.finished.then(() => settle(open), () => {})
}

export function animateDisclosure(node: HTMLDetailsElement): void {
  node.querySelector('summary')?.addEventListener('click', (event) => {
    if (!messageView.extra || reducedMotion()) return
    event.preventDefault()
    setDisclosure(node, !node.open || node.dataset.closing === 'true')
  })
}

export function smallMotion(node: HTMLElement | null, kind: 'arrive' | 'count' | 'dates'): void {
  if (!node || reducedMotion() || typeof node.animate !== 'function') return
  if (kind === 'arrive' ? !messageView.extra : !messageView.feedback) return
  const frames = kind === 'arrive'
    ? [{ backgroundColor: '#fff6ce', opacity: .65, transform: 'translateY(6px)' }, { backgroundColor: '#fff6ce', opacity: 1, transform: 'translateY(0)', offset: .3 }, { backgroundColor: 'transparent', opacity: 1, transform: 'translateY(0)' }]
    : kind === 'count'
      ? [{ transform: 'scale(1)' }, { transform: 'scale(1.06)' }, { transform: 'scale(1)' }]
      : [{ opacity: .9, transform: 'translateY(4px)' }, { opacity: 1, transform: 'translateY(0)' }]
  node.animate(frames, { duration: kind === 'arrive' ? 750 : 200, easing: 'ease-out' })
}

export interface DateChoice { date: string; label: string; detail?: string }

function dateIcon(kind: 'down'): SVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  svg.setAttribute('viewBox', '0 0 24 24')
  svg.setAttribute('aria-hidden', 'true')
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
  path.setAttribute('d', kind === 'down' ? 'm6 9 6 6 6-6' : ''); svg.append(path)
  return svg
}
// Design B (Liam, 23 Oct 2026: "I like B"): where they are sits beside the heading (`aside`), and one line under the
// days says what the message offers, where a hint and a count that looked like a tick box sat.
export function quickDatePicker(choices: DateChoice[], selected: string[], change: (dates: string[]) => void, aside?: string | HTMLElement): HTMLElement {
  const head = el('div', { class: 'messages-date-head' }, el('span', { class: 'rebook-fact-label' }, 'Days to offer'))
  if (aside) head.append(el('span', { class: 'messages-date-aside' }, lineDrawing('house', 15), aside))
  const box = el('div', { class: 'messages-dates messages-date-section' }, head)
  const picked = new Set(selected)
  const countText = el('strong', {})
  const hint = el('span', { class: 'messages-date-hint' })
  const count = el('p', { class: 'messages-date-count', role: 'status' }, lineDrawing('calendarTick', 18), el('span', {}, countText, hint))
  const controls: { date: string; input: HTMLInputElement | HTMLButtonElement }[] = []
  const details = choices.length > 3 ? el('details', { class: 'messages-date-dropdown' }) : null
  const summaryText = el('span', {})
  const summary = details ? el('summary', { 'aria-label': 'Choose days to offer' }, summaryText, dateIcon('down')) : null
  const group = el('div', { class: details ? 'messages-date-list' : 'messages-date-buttons', role: 'group', 'aria-label': 'Days to offer' })
  const paint = (): void => {
    const dates = choices.filter((c) => picked.has(c.date))
    for (const { date, input } of controls) {
      if (input.tagName === 'INPUT') (input as HTMLInputElement).checked = picked.has(date)
      else input.setAttribute('aria-pressed', String(picked.has(date)))
    }
    count.dataset.multiple = String(dates.length > 1)
    countText.textContent = dates.length === 1 ? `Offering ${dates[0]!.label}.` : dates.length ? `Offering ${dates.length} days: ${dates.map((d) => d.label).join(', ')}.` : 'Choose at least one day.'
    hint.textContent = dates.length > 1 ? ' They choose; book their visit after they reply.' : dates.length ? ' Tap another day to let them choose.' : ''
    summaryText.textContent = dates.length === 1 ? dates[0]!.label : dates.length ? `${dates.length} dates to offer` : 'Choose days to offer'
  }
  for (const choice of choices) {
    const choose = (): void => {
      if (picked.has(choice.date)) picked.delete(choice.date); else picked.add(choice.date)
      paint()
      change(choices.filter((c) => picked.has(c.date)).map((c) => c.date))
      if (picked.size > 1) smallMotion(count, 'dates')
    }
    if (details) {
      const input = el('input', { type: 'checkbox', 'data-filter': 'true', 'aria-label': [choice.label, choice.detail].filter(Boolean).join(' · ') })
      input.addEventListener('change', choose)
      controls.push({ date: choice.date, input })
      group.append(el('label', {}, input, el('span', {}, el('strong', {}, choice.label), ...(choice.detail ? [el('small', {}, choice.detail)] : []))))
    } else {
      const input = el('button', { type: 'button', class: 'messages-date-button', 'aria-pressed': String(picked.has(choice.date)) }, el('strong', {}, choice.label), ...(choice.detail ? [el('span', {}, choice.detail)] : []))
      input.addEventListener('click', choose)
      controls.push({ date: choice.date, input }); group.append(input)
    }
  }
  if (details && summary) {
    details.append(summary, group); animateDisclosure(details)
    details.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return
      event.preventDefault(); setDisclosure(details, false); summary.focus()
    })
    box.append(details)
  } else box.append(group)
  box.append(count); paint()
  return box
}

// Native date dropdowns close on an outside tap; the selected values remain.
document.addEventListener('click', (event) => {
  for (const node of document.querySelectorAll<HTMLDetailsElement>('.messages-date-dropdown')) {
    if (node.open && !node.contains(event.target as Node)) setDisclosure(node, false)
  }
})
